import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import {
  Info,
  MapPin,
  Phone,
  UnfoldVertical,
  FoldVertical,
} from "lucide-react";

import React, { useState } from "react";
import RateDetails from "./rate-detail";
import { ModalIdentifiers, POS_LABEL_MAPPER } from "@/constants";
import type { PlaceOfService, ProviderCost } from "@/model/provider-cost";
import { ProviderCostView } from "@/model/provider-cost";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import { formatPhoneNumber } from "@/lib/phone-utils";

const TxTesultItem: React.FC<{
  index: number;
  open: boolean;
  value: ProviderCost;
  onMapView: (index: number) => void;
  onButtonClick: (
    identifier: ModalIdentifiers,
    item?: ProviderCost,
    posItem?: PlaceOfService,
  ) => void;
  deductibleStatus: boolean;
}> = ({ index, open, value, onButtonClick, onMapView, deductibleStatus }) => {
  const [isExpanded, setIsExpanded] = useState(open);
  const providerCostViewModel = new ProviderCostView(value);
  const { transformedNetworkTier } = useSelector(
    (state: RootState) => state.priceTransparency,
  );

  return (
    <Card className="mb-10 rounded-none border-x-0">
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CollapsibleTrigger asChild>
          <CardHeader className="h-12 justify-center border-b hover:cursor-pointer">
            <div className="flex items-center justify-between">
              <CardTitle as="h2" className="text-lg">
                {providerCostViewModel.providerName()}
              </CardTitle>
              <div className="flex items-center justify-between gap-4">
                {isExpanded ? (
                  <FoldVertical className="size-4" />
                ) : (
                  <UnfoldVertical className="size-4" />
                )}
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CardContent>
          <div className="mt-4 flex items-start justify-between">
            <div className="flex flex-col items-start justify-start gap-6">
              <div className="justify-start space-x-1 overflow-x-auto">
                {providerCostViewModel
                  ?.specialityDetails()
                  .map((speciality) => (
                    <Badge
                      key={speciality.description}
                      className="bg-slate-200 text-muted-foreground"
                    >
                      {speciality.description?.toUpperCase()}
                    </Badge>
                  ))}
              </div>

              <div className="flex flex-col items-start justify-start">
                <Label className="text-md font-bold uppercase text-black">
                  {providerCostViewModel.employerName()}
                </Label>
                <Label className="text-sm font-semibold uppercase text-black">
                  {providerCostViewModel.address()}
                </Label>
                <Label className="text-sm font-semibold text-muted-foreground">
                  {Math.floor(providerCostViewModel.distance())} Miles (Approx.)
                </Label>
                <p className="text-sm font-semibold uppercase text-black">
                  {formatPhoneNumber(providerCostViewModel?.providerPhone())}
                </p>
              </div>
            </div>

            <div className="flex flex-col items-end justify-end gap-6">
              <Badge
                style={{
                  backgroundColor: transformedNetworkTier
                    ? transformedNetworkTier[
                        providerCostViewModel.networkName()
                      ].color
                    : "#00C141",
                  color: "#ffffff",
                }}
              >
                {providerCostViewModel.networkTag()}
              </Badge>
              <div className="flex w-full flex-col items-center justify-end gap-0">
                {/*
                  2.1.1 Keyboard — A / 4.1.2 Name, Role, Value — A. The cost-breakdown
                  trigger was a <div onClick>, so a member could not open the breakdown of
                  what they are being asked to pay without a mouse. <Title> also emitted an
                  <h1> at the time, which would have placed a level-one heading inside a
                  control. Title now takes an explicit level, but the cost figure on a
                  button is not a heading at any level — a <span> carries the same visual
                  weight.
                */}
                <button
                  type="button"
                  aria-label={`View cost breakdown, total ${providerCostViewModel.overallProcedureCost()}`}
                  className="flex items-center gap-2 rounded hover:cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  onClick={() =>
                    onButtonClick(
                      ModalIdentifiers.COLLAPSED_COST_BREAKDOWN_CONTAINER,
                      value,
                    )
                  }
                >
                  <span className="mb-1 text-2xl font-bold leading-relaxed text-black">
                    {providerCostViewModel.overallProcedureCost()}
                  </span>
                  <Info
                    aria-hidden="true"
                    className="size-6 rounded-full bg-yellow-500 text-white"
                  />
                </button>
                <Label className="text-medium text-muted-foreground">
                  Your Total Estimated Cost
                </Label>
              </div>

              <div className="flex w-full flex-row items-center justify-end gap-2">
                {/*
                  2.1.1 Keyboard — A / 4.1.2 Name, Role, Value — A. All three were bare
                  Lucide SVGs with onClick handlers: not focusable, no role, no accessible
                  name. A screen reader user browsing cost-estimate results could neither
                  reach them nor tell call from map from details. Names include the
                  provider so they are distinguishable between rows (2.4.4 — A).
                */}
                {Boolean(providerCostViewModel.providerPhone()) && (
                  <button
                    type="button"
                    aria-label={`Call ${providerCostViewModel.providerName?.() ?? "provider"}`}
                    className="size-8 rounded-md bg-primary p-2 text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    onClick={() => {
                      window.location.href = `tel:${providerCostViewModel.providerPhone()}`;
                    }}
                  >
                    <Phone aria-hidden="true" className="size-full" />
                  </button>
                )}
                <button
                  type="button"
                  aria-label={`Show ${providerCostViewModel.providerName?.() ?? "provider"} on the map`}
                  className="size-8 rounded-md bg-primary p-2 text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  onClick={() => onMapView(index)}
                >
                  <MapPin aria-hidden="true" className="size-full" />
                </button>
                <button
                  type="button"
                  aria-label={`Provider details for ${providerCostViewModel.providerName?.() ?? "provider"}`}
                  className="size-8 rounded-md bg-primary p-2 text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  onClick={() =>
                    onButtonClick(ModalIdentifiers.PROVIDER_INFO, value)
                  }
                >
                  <Info aria-hidden="true" className="size-full" />
                </button>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <CollapsibleContent>
              <Tabs
                defaultValue={providerCostViewModel.defaultPlaceOfService()}
                className="w-full"
              >
                <div className="hide-scrollbar overflow-x-auto border-b">
                  <TabsList className="grid w-full min-w-max grid-cols-7 gap-2 border-b bg-transparent">
                    {value.placeOfServices.map((item: PlaceOfService) => (
                      <TabsTrigger
                        key={item.type}
                        value={item.type}
                        className="min-w-fit rounded-b-none border-transparent p-2 font-medium capitalize"
                      >
                        {POS_LABEL_MAPPER[item.type]}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </div>

                {providerCostViewModel
                  .placeOfServices()
                  .map((item: PlaceOfService) => (
                    <TabsContent key={item.type} value={item.type}>
                      <div className="flex flex-col gap-2">
                        <div className="flex w-full flex-col gap-4 rounded-lg bg-gray-100 p-4 shadow-sm">
                          <RateDetails
                            rateInfo={providerCostViewModel.placeOfServiceRates(
                              item.services,
                            )}
                            isBundle={
                              item.negotiateArrangement?.toUpperCase() ===
                              "BUNDLE"
                            }
                            viewCallback={() =>
                              onButtonClick(ModalIdentifiers.SPEND_SUMMARY)
                            }
                            costBreakdownCallback={() =>
                              onButtonClick(
                                ModalIdentifiers.POS_COST_BREAKDOWN_CONTAINER,
                                value,
                                item,
                              )
                            }
                            deductibleStatus={deductibleStatus}
                          />
                        </div>
                        <div className="flex flex-col items-start">
                          <div className="flex items-center justify-center space-x-2">
                            <Info className="size-4 text-primary" />
                            <Label className="pt-1 text-sm text-muted-foreground">
                              Prior Authorization may be required
                            </Label>
                          </div>
                          <div className="flex items-center justify-center space-x-2">
                            <Info className="size-4 text-primary" />
                            <Label className="pt-1 text-sm text-muted-foreground">
                              Visit limitation may exist
                            </Label>
                          </div>
                        </div>
                      </div>
                    </TabsContent>
                  ))}
              </Tabs>
            </CollapsibleContent>
          </div>
        </CardContent>
      </Collapsible>
    </Card>
  );
};

export default TxTesultItem;
